/**
 * Settings > Checklist Templates (tracker item 44).
 *
 * Reusable job checklists: a name, an active flag and 1-100 ordered steps
 * (text up to 500 characters, optional "required"). A template linked to an
 * Item is copied onto a Job when that item is added. Saving replaces the whole
 * step set (PUT). Delete removes an unused template and deactivates one that
 * any item or job checklist references; the page reports which happened from
 * the server's `result`. Route and card are ADMIN / OFFICE_MANAGER only
 * (canManageChecklistTemplates).
 */

import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IChecklistTemplatesApi } from '../../api';
import * as types from '../../api/types';
import { StarterMark, useStarterRecords } from '../../components/StarterBadge';
import { Toast } from '../../components/Toast';
import {
  inputClass,
  labelClass,
  primaryButtonClass,
  rowActionClass,
  rowDangerActionClass,
  secondaryButtonClass,
} from '../../components/formStyles';
import { getErrorCode, getErrorMessage } from '../../utils/apiError';
import {
  ChecklistTemplateFormState,
  buildChecklistTemplateRequest,
  checklistTemplateFormFromTemplate,
  deleteConfirmMessage,
  deleteOutcomeMessage,
  emptyChecklistTemplateForm,
  moveStep,
  newStepDraft,
  validateChecklistTemplateForm,
} from '../../utils/checklistTemplate';

type StatusFilter = 'active' | 'inactive' | 'all';
type Editing = { mode: 'create' } | { mode: 'edit'; template: types.ChecklistTemplate } | null;

const FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];

function filterToQuery(filter: StatusFilter): types.ChecklistTemplateListFilters {
  if (filter === 'active') return { active: true };
  if (filter === 'inactive') return { active: false };
  return {};
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

export function ChecklistTemplatesPage() {
  const api = useMemo(() => createAPIClient() as IChecklistTemplatesApi, []);
  const starter = useStarterRecords('CHECKLIST_TEMPLATE');
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [templates, setTemplates] = useState<types.ChecklistTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState<ChecklistTemplateFormState>(emptyChecklistTemplateForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const loadRequest = useRef(0);

  const load = useCallback(async (status: StatusFilter) => {
    const request = ++loadRequest.current;
    setIsLoading(true);
    try {
      const rows = await api.listChecklistTemplates(filterToQuery(status));
      if (request === loadRequest.current) setTemplates(rows);
    } catch (err) {
      if (request === loadRequest.current) setError(getErrorMessage(err, 'Could not load checklist templates'));
    } finally {
      if (request === loadRequest.current) setIsLoading(false);
    }
  }, [api]);

  useEffect(() => { load(filter); }, [load, filter]);

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm(emptyChecklistTemplateForm());
    setFormError(null);
    setNotice(null);
  };

  const openEdit = (template: types.ChecklistTemplate) => {
    setEditing({ mode: 'edit', template });
    setForm(checklistTemplateFormFromTemplate(template));
    setFormError(null);
    setNotice(null);
  };

  const closeForm = () => {
    setEditing(null);
    setFormError(null);
  };

  const updateStep = (key: string, patch: Partial<{ text: string; required: boolean }>) => {
    setForm((prev) => ({ ...prev, steps: prev.steps.map((step) => (step.key === key ? { ...step, ...patch } : step)) }));
  };

  const addStep = () => {
    setForm((prev) => (prev.steps.length >= types.CHECKLIST_TEMPLATE_MAX_STEPS
      ? prev
      : { ...prev, steps: [...prev.steps, newStepDraft()] }));
  };

  const removeStep = (key: string) => {
    setForm((prev) => (prev.steps.length <= 1 ? prev : { ...prev, steps: prev.steps.filter((step) => step.key !== key) }));
  };

  const shiftStep = (index: number, delta: number) => {
    setForm((prev) => ({ ...prev, steps: moveStep(prev.steps, index, delta) }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    const validationError = validateChecklistTemplateForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    const request = buildChecklistTemplateRequest(form);
    setIsSaving(true);
    setFormError(null);
    try {
      if (editing.mode === 'edit') {
        await api.replaceChecklistTemplate(editing.template.id, request);
        setNotice(`Checklist template "${request.name}" saved.`);
      } else {
        await api.createChecklistTemplate(request);
        setNotice(`Checklist template "${request.name}" created.`);
      }
      setEditing(null);
      await load(filter);
    } catch (err) {
      const code = getErrorCode(err);
      if (code === 'DUPLICATE_CHECKLIST_TEMPLATE') {
        setFormError(
          `A checklist template named "${request.name}" already exists. It may be inactive: ` +
            'set the filter to Inactive or All to find it, then rename or reactivate it.'
        );
      } else if (code === 'NOT_FOUND') {
        setFormError('This checklist template no longer exists. Close the form and refresh the list.');
        await load(filter);
      } else {
        setFormError(getErrorMessage(err, editing.mode === 'edit' ? 'Could not save checklist template' : 'Could not create checklist template'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (template: types.ChecklistTemplate) => {
    if (!window.confirm(deleteConfirmMessage(template))) return;
    setBusyId(template.id);
    setError(null);
    setNotice(null);
    try {
      const outcome = await api.deleteChecklistTemplate(template.id);
      setNotice(deleteOutcomeMessage(outcome, template.name));
      if (editing?.mode === 'edit' && editing.template.id === template.id) closeForm();
      await load(filter);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not delete checklist template'));
    } finally {
      setBusyId(null);
    }
  };

  const stepCount = form.steps.length;
  const atStepLimit = stepCount >= types.CHECKLIST_TEMPLATE_MAX_STEPS;

  return (
    <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link to="/settings" className="text-sm font-semibold text-gray-500 hover:text-blue-600">← Settings</Link>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">Checklist Templates</h1>
          <p className="mt-1 text-sm text-gray-600">
            Reusable job checklists. Link a template to an item on the{' '}
            <Link to="/settings/items" className="font-semibold text-blue-600 hover:text-blue-700">Items</Link>{' '}
            page and its steps are copied onto every job that item is added to. Editing a template never changes
            existing jobs.
          </p>
        </div>
        {!editing && (
          <button type="button" onClick={openCreate} className={`${primaryButtonClass} w-full sm:w-auto sm:shrink-0`}>
            New template
          </button>
        )}
      </div>

      {notice && <Toast kind="success">{notice}</Toast>}
      {error && <Toast kind="error">{error}</Toast>}

      {editing && (
        <section className="mb-6 rounded-lg bg-white shadow" aria-label="Checklist template form">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-6">
              <h2 className="mb-4 text-base font-semibold text-gray-900">
                {editing.mode === 'edit' ? `Edit ${editing.template.name}` : 'New checklist template'}
              </h2>
              {formError && <Toast kind="error">{formError}</Toast>}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <div className="sm:col-span-4">
                  <label htmlFor="checklistTemplateName" className={labelClass}>
                    Name<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <input
                    id="checklistTemplateName"
                    type="text"
                    value={form.name}
                    maxLength={types.CHECKLIST_TEMPLATE_NAME_MAX}
                    disabled={isSaving}
                    placeholder="Weekly pool service"
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div className="flex items-end sm:col-span-2">
                  <label htmlFor="checklistTemplateActive" className="inline-flex items-center gap-2 py-2.5 text-sm text-gray-700">
                    <input
                      id="checklistTemplateActive"
                      type="checkbox"
                      checked={form.isActive}
                      disabled={isSaving}
                      onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="font-medium">Active</span>
                  </label>
                </div>
              </div>
              {!form.isActive && (
                <p className="mt-2 text-xs text-gray-500">Inactive templates cannot be linked to items or applied to jobs. Jobs that already have the steps keep them.</p>
              )}

              <fieldset className="mt-6" disabled={isSaving}>
                <legend className="text-sm font-semibold text-gray-900">
                  Steps <span className="font-normal text-gray-500">({stepCount} of {types.CHECKLIST_TEMPLATE_MAX_STEPS})</span>
                </legend>
                <ol className="mt-3 space-y-3">
                  {form.steps.map((step, index) => {
                    const position = index + 1;
                    return (
                      <li key={step.key} className="flex flex-col gap-2 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-center">
                        <span className="w-8 shrink-0 text-sm font-semibold text-gray-500">{position}.</span>
                        <input
                          type="text"
                          aria-label={`Step ${position} text`}
                          value={step.text}
                          maxLength={types.CHECKLIST_STEP_TEXT_MAX}
                          placeholder="Describe the step"
                          onChange={(e) => updateStep(step.key, { text: e.target.value })}
                          className={`${inputClass} sm:flex-1`}
                        />
                        <div className="flex flex-wrap items-center gap-3">
                          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              aria-label={`Step ${position} required`}
                              checked={step.required}
                              onChange={(e) => updateStep(step.key, { required: e.target.checked })}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            Required
                          </label>
                          <button type="button" className={rowActionClass} aria-label={`Move step ${position} up`} disabled={index === 0} onClick={() => shiftStep(index, -1)}>↑</button>
                          <button type="button" className={rowActionClass} aria-label={`Move step ${position} down`} disabled={index === stepCount - 1} onClick={() => shiftStep(index, 1)}>↓</button>
                          <button type="button" className={rowDangerActionClass} aria-label={`Remove step ${position}`} disabled={stepCount <= 1} onClick={() => removeStep(step.key)}>Remove</button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button type="button" className={secondaryButtonClass} disabled={atStepLimit} onClick={addStep}>Add step</button>
                  <span className="text-xs text-gray-500">
                    {atStepLimit
                      ? `A template can have at most ${types.CHECKLIST_TEMPLATE_MAX_STEPS} steps.`
                      : 'Required steps are flagged on the job; they do not block completing it.'}
                  </span>
                </div>
              </fieldset>
            </div>

            <div className="flex flex-col-reverse gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button type="button" onClick={closeForm} disabled={isSaving} className={`${secondaryButtonClass} w-full sm:w-auto`}>Cancel</button>
              <button type="submit" disabled={isSaving} className={`${primaryButtonClass} w-full sm:w-auto`}>
                {isSaving ? 'Saving...' : editing.mode === 'edit' ? 'Save template' : 'Create template'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-label="Checklist templates">
        <div className="mb-4 flex items-end gap-3">
          <div>
            <label htmlFor="checklistTemplateFilter" className={labelClass}>Show</label>
            <select
              id="checklistTemplateFilter"
              value={filter}
              onChange={(e) => setFilter(e.target.value as StatusFilter)}
              className={inputClass}
            >
              {FILTER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
        </div>

        {isLoading ? (
          <p className="py-8 text-center text-sm text-gray-600">Loading checklist templates...</p>
        ) : templates.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-600">
            {filter === 'active' ? 'No active checklist templates yet.' : filter === 'inactive' ? 'No inactive checklist templates.' : 'No checklist templates yet.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {templates.map((template) => {
              const busy = busyId === template.id;
              // Deleting an inactive template that is still in use would change nothing.
              const canDelete = template.isActive || !template.inUse;
              return (
                <li key={template.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 break-words">
                    <div className={`font-semibold ${template.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                      {template.name}
                      {!template.isActive && <span className="ml-2 text-xs font-normal text-gray-500">Inactive</span>}
                    </div>
                    <StarterMark records={starter} entityId={template.id} name={template.name} />
                    <div className="text-sm text-gray-500">
                      {plural(template.stepCount, 'step')}
                      {template.steps.some((step) => step.required) && ` (${template.steps.filter((step) => step.required).length} required)`}
                      {' · '}
                      {template.inUse
                        ? `Used by ${plural(template.itemCount, 'item')} and ${plural(template.jobStepCount, 'job step')}`
                        : 'Not used yet'}
                    </div>
                  </div>
                  <span className="flex shrink-0 gap-4">
                    <button type="button" className={rowActionClass} disabled={isSaving || busy} onClick={() => openEdit(template)} aria-label={`Edit ${template.name}`}>Edit</button>
                    {canDelete && (
                      <button type="button" className={rowDangerActionClass} disabled={busy || busyId !== null} onClick={() => handleDelete(template)} aria-label={`${template.inUse ? 'Deactivate' : 'Delete'} ${template.name}`}>
                        {busy ? 'Working...' : template.inUse ? 'Deactivate' : 'Delete'}
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
